import { Controller } from "@hotwired/stimulus"

// Drag-and-drop audio file controller for DPK album tracks.
// Accepts MP3 and WAV files, creates track rows in the nested form.
export default class extends Controller {
  static targets = ["dropZone", "fileInput", "container"]

  connect() {
    this.allowedTypes = ["audio/mpeg", "audio/mp3", "audio/wav", "audio/x-wav", "audio/wave"]
  }

  // --- Drag and drop ---

  dragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.add("border-[var(--color-mc-accent-terracotta)]", "bg-[var(--color-mc-accent-terracotta)]/5")
  }

  dragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.remove("border-[var(--color-mc-accent-terracotta)]", "bg-[var(--color-mc-accent-terracotta)]/5")
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.remove("border-[var(--color-mc-accent-terracotta)]", "bg-[var(--color-mc-accent-terracotta)]/5")
    this.addFiles(Array.from(event.dataTransfer.files))
  }

  selectFiles(event) {
    if (event) event.stopPropagation()
    this.fileInputTarget.click()
  }

  filesSelected(event) {
    this.addFiles(Array.from(event.target.files))
    event.target.value = ""
  }

  // --- File processing ---

  addFiles(files) {
    files.forEach(file => {
      if (!this.allowedTypes.includes(file.type)) {
        if (window.showToast) window.showToast("error", `${file.name}: Only MP3 and WAV files are accepted.`)
        return
      }

      const isWav = file.type.includes("wav")
      if (isWav && window.showToast) {
        window.showToast("info", `${file.name} is a WAV file and will be converted to MP3 after upload.`)
      }

      this.createTrackRow(file, isWav)
    })
  }

  createTrackRow(file, isWav) {
    // Find the nested-form template and clone it
    const template = this.element.querySelector("template[data-nested-form-target='template']")
    if (!template) return

    const container = this.containerTarget
    // Collision-proof AND numeric: Rails strong params only accept nested
    // rows whose key is all digits (/\A-?\d+\z/), so UUIDs are silently
    // dropped. A page-global monotonic counter (shared with nested-form,
    // seeded by the clock) can never mint the same key twice - a multi-file
    // EP drop in one millisecond previously collided and lost tracks.
    const index = window.__nestedRowIndex = Math.max(Date.now(), (window.__nestedRowIndex || 0) + 1)
    const content = template.content.cloneNode(true)
    const wrapper = content.querySelector(".track-fields")
    if (!wrapper) return

    // Replace NEW_RECORD placeholders with unique index
    wrapper.innerHTML = wrapper.innerHTML.replace(/NEW_RECORD/g, index)

    // Pre-fill title from filename (strip extension)
    const titleInput = wrapper.querySelector("input[name*='[title]']")
    if (titleInput) {
      const baseName = file.name.replace(/\.[^.]+$/, "")
      titleInput.value = baseName
    }

    // Set the file input
    const fileInput = wrapper.querySelector("input[type='file']")
    if (fileInput) {
      const dt = new DataTransfer()
      dt.items.add(file)
      fileInput.files = dt.files
    }

    // Auto-detect duration via AudioContext, fill it in when ready
    const durationInput = wrapper.querySelector("input[name*='[duration]']")
    this.detectDuration(file).then(duration => {
      if (durationInput && duration) {
        durationInput.value = duration
        durationInput.placeholder = duration
      }
    })

    // Add WAV indicator
    if (isWav) {
      const indicator = document.createElement("p")
      indicator.className = "text-xs text-[var(--color-status-pending)]"
      indicator.textContent = "WAV file - will be converted to MP3 after upload"
      wrapper.appendChild(indicator)
    }

    // Add file size info
    const sizeInfo = document.createElement("p")
    sizeInfo.className = "text-xs text-[var(--color-mc-text-secondary)]"
    sizeInfo.textContent = `File: ${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)`
    wrapper.appendChild(sizeInfo)

    container.appendChild(wrapper)

    // Scroll the new track into view
    wrapper.scrollIntoView({ behavior: "smooth", block: "center" })

    if (window.showToast) window.showToast("success", `Added "${file.name}" as a new track.`)
  }

  async detectDuration(file) {
    try {
      const arrayBuffer = await file.arrayBuffer()
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)()
      const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer)
      const totalSeconds = Math.round(audioBuffer.duration)
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = totalSeconds % 60
      audioCtx.close()
      return `${minutes}:${seconds.toString().padStart(2, "0")}`
    } catch {
      return null
    }
  }
}
